import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useSearchParams } from "react-router";

import {
  emptyString,
  screenResize,
  customerDetail,
  loginConstants,
  surveyQuestionsConstants,
  IS_POS_QUESTION,
  IS_NPS_FIRST,
  fiveStar,
  fourStar,
  NPS_FIRST_QUESTION_ORDER,
  customerSurveyConstants,
  customerSurveyLabels,
} from "../../constants";
import { getItemFromStorage } from "../../../../system";
import {
  customerSurveyActions,
  surveyQuestionActions,
} from "../../redux/actions";

import { useSpinner, isSuperOrBrandAdmin, Spinner } from "../../../../shared";
import SurveyQuestionRenderer from "../customerSurvey/customerQuestionType/shared/surveyQuestionRenderer";
import SurveyLogoBanner from "../customerSurvey/customerQuestionType/shared/surveyLogoBanner";
import SurveyStatusMessage from "../customerSurvey/customerQuestionType/shared/surveyStatusMessage";
import UnsupportedQuestionType from "../customerSurvey/customerQuestionType/shared/unsupportedQuestionType";
import { applySurveyThemeStyles } from "../customerSurvey/utils/applySurveyThemeStyles";

const {
  TRUE_VALUE,
  DEFAULT_QUESTION_PAGE,
  DEFAULT_PREVIEW_MAIN_THEME_COLOR,
  MIN_QUESTIONS_TO_RENDER,
} = customerSurveyConstants;

const PreviewSurvey = (props) => {
  const {
    user,
    surveyTheme,
    brandDetail,
    getThemeByBrandId,
    fetchSurveyQuestions,
    questionsAnswerArray,
    getBrandDetailByBrandId,
  } = props;

  const { mainThemeColor, fontColor, fontStyle, progressBarColor } =
    surveyTheme || {};

  const brandId = isSuperOrBrandAdmin()
    ? getItemFromStorage(loginConstants.BRAND_ID)
    : user.brandId;

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [surveyCompleted, setSurveyCompleted] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(
    DEFAULT_QUESTION_PAGE,
  );
  const [customerQuestions, setCustomerQuestions] = useState([
    ...(questionsAnswerArray || []),
  ]);
  const [isFiveStarOnNPS, setIsFiveStarOnNPS] = useState(false);

  const [searchParams] = useSearchParams();
  const isPOSQuestion = searchParams.get(IS_POS_QUESTION) === TRUE_VALUE;
  const isNPSFirst = searchParams.get(IS_NPS_FIRST) === TRUE_VALUE;

  // Show loading while a request is running.
  const spinner = useSpinner();

  const isSpinnerRunning = () => {
    return (
      spinner(surveyQuestionsConstants.SURVEY_SCREEN_SPINNER) ||
      spinner(surveyQuestionsConstants.GET_BRAND_DETAIL_BY_ID)
    );
  };

  //Function to handle back button
  const handleBackButton = () => {
    setCurrentQuestion((prev) =>
      prev > DEFAULT_QUESTION_PAGE ? prev - 1 : prev,
    );
  };

  //Function to handle next button
  const handleNextButton = () => {
    setCurrentQuestion((prev) =>
      prev + 1 < (customerQuestions?.length || 0) ? prev + 1 : prev,
    );
  };

  const handleSubmit = () => {
    //Handle submit function
    setSurveyCompleted(true);
  };

  //Function to check if current question is NPS question and user gave 4 or 5 stars
  const checkFiveStarOnNPS = (rating) => {
    const currentQuestionData = customerQuestions?.[currentQuestion];

    setIsFiveStarOnNPS(
      isNPSFirst &&
        currentQuestionData?.questionOrder === NPS_FIRST_QUESTION_ORDER &&
        (rating === Number(fiveStar) || rating === Number(fourStar)),
    );
  };

  useEffect(() => {
    getSurveyQuestions(isSuperOrBrandAdmin() ? brandId : user.brandId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getSurveyQuestions = (brandId) => {
    // Determine whether the active tab is onlineOrder or inStore
    const dataToSend = {
      brandId: brandId,
      isPOSQuestion: isPOSQuestion,
    };

    // Pass the data to the fetchSurveyQuestions API call
    fetchSurveyQuestions(dataToSend);
  };

  //Getting window innerwidth for removing and adding logo background
  useEffect(() => {
    const handleWindowResize = () => {
      setWindowWidth(window.innerWidth);
    };
    //Add event listener for screen resize
    window.addEventListener(screenResize, handleWindowResize);

    //Remove event listener for screen resize
    return () => {
      window.removeEventListener(screenResize, handleWindowResize);
    };
    // eslint-disable-next-line
  }, []);

  //Get survey against brandId when the page renders
  useEffect(() => {
    if (
      typeof questionsAnswerArray === surveyQuestionsConstants.OBJECT &&
      Array.isArray(questionsAnswerArray)
    ) {
      // If NPS First is active, move NPS question to the top
      if (isNPSFirst) {
        const updatedQuestionsArray = questionsAnswerArray.map((q) => {
          return {
            ...q,
            ...(q.isSentimentQuestion && {
              questionOrder: NPS_FIRST_QUESTION_ORDER,
            }),
          };
        });
        // Sort by questionOrder to move NPS question to the start
        const sortedQuestionsArray = updatedQuestionsArray.sort(
          (a, b) => a.questionOrder - b.questionOrder,
        );
        setCustomerQuestions(sortedQuestionsArray);
      } else {
        setCustomerQuestions(questionsAnswerArray);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionsAnswerArray, isNPSFirst]); //Getting survey theme and brand logo  for survey preview
  useEffect(() => {
    getThemeByBrandId(brandId);
    getBrandDetailByBrandId(brandId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  //Update survey preview theme which is set by admin
  useEffect(() => {
    applySurveyThemeStyles({
      mainThemeColor,
      fontColor,
      fontStyle,
      progressBarColor,
      defaultMainThemeColor: DEFAULT_PREVIEW_MAIN_THEME_COLOR,
    });
  }, [mainThemeColor, fontColor, fontStyle, progressBarColor]);

  if (surveyCompleted) {
    return (
      <SurveyStatusMessage
        title={customerSurveyLabels.SURVEY_SUBMITTED}
        showHeadingLogo
        brandDetail={brandDetail}
        showCheckIcon
        containerClassName="d-flex flex-column align-items-center question-container custom-height w-100 py-2"
        titleClassName="fw-bolder text-center text-black"
      />
    );
  }
  if (isSpinnerRunning()) {
    return <Spinner />;
  }

  if (
    Array.isArray(customerQuestions) &&
    customerQuestions.length > MIN_QUESTIONS_TO_RENDER
  ) {
    const currentQuestionData = customerQuestions?.[currentQuestion];

    if (!currentQuestionData) {
      return <UnsupportedQuestionType />;
    }

    return (
      <SurveyQuestionRenderer
        question={currentQuestionData}
        currentQuestion={currentQuestion}
        questionArray={customerQuestions}
        handleNextButton={handleNextButton}
        handleBackButton={handleBackButton}
        windowWidth={windowWidth}
        customerDetail={customerDetail}
        surveyTheme={surveyTheme || {}}
        brandDetail={brandDetail}
        setCustomerQuestions={setCustomerQuestions}
        setSurveyCompleted={setSurveyCompleted}
        handleSubmitSurvey={handleSubmit}
        isNPSFirstActive={isNPSFirst}
        isFiveStarOnNPS={isFiveStarOnNPS}
        checkFiveStarOnNPS={checkFiveStarOnNPS}
      />
    );
  }

  return (
    <div className="d-flex flex-column align-items-center vh-100">
      <SurveyLogoBanner brandDetail={brandDetail} windowWidth={windowWidth} />
      <div className="my-auto">{emptyString}</div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.authentication.user,
  surveyTheme: state.customerSurveyReducer.surveyTheme,
  brandDetail: state.customerSurveyReducer.brandDetail,
  questionsAnswerArray: state.surveyQuestionsReducer.questionsAnswerArray,
});

const mapDispatchToProps = (dispatch) => ({
  fetchSurveyQuestions: (brandId) =>
    dispatch(surveyQuestionActions.getSurveyQuestions(brandId)),
  getThemeByBrandId: (brandId) =>
    dispatch(customerSurveyActions.getThemeByBrandId(brandId)),
  getBrandDetailByBrandId: (brandId) =>
    dispatch(customerSurveyActions.getBrandDetailByBrandId(brandId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PreviewSurvey);
